/**
 * Custom 360 View Web Component
 * Independent variant selection with color swatches
 */

if (!customElements.get('custom-360-view')) {
  class Custom360View extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productHandle = this.dataset.productHandle;
      this.viewer360Container = null;
      this.cloudimageElement = null;
      this.viewerWrapper = null;
      this.loadingTimeout = null;
      this.isInitializing = false;
      this.currentVariantId = null;
      this.allVariants = [];
      this.editionSelect = null;
      this.colorSwatchesContainer = null;
      this.colorDisplay = null;
    }

    connectedCallback() {
      this.cacheDOMElements();
      this.loadProductVariants();
      this.init360Viewer();
      this.getCurrentVariant();
      this.initVariantSelectors();
      this.checkInitialAudioVisibility(); // ← add this line
      this.initAudioPlayer();
    }

    disconnectedCallback() {
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
      }
      // Remove event listeners
      if (this.editionSelect) {
        this.editionSelect.removeEventListener('change', this.handleEditionChange);
      }
      this.removeColorSwatchListeners();
    }

    cacheDOMElements() {
      this.viewer360Container = this.querySelector('[data-360-container]');
      this.cloudimageElement = this.querySelector('.cloudimage-360');
      this.viewerWrapper = this.querySelector('.custom-360-viewer');
      this.currentVariantScript = this.querySelector('[data-current-variant]');
      this.productVariantsScript = this.querySelector('[data-product-variants]');
      
      // Variant selectors
      this.editionSelect = this.querySelector('[data-edition-select]');
      this.colorSwatchesContainer = this.querySelector('[data-360-color-swatches]');
      this.colorDisplay = this.querySelector('[data-360-color-display]');

      // Audio elements
      this.audioPlayer = this.querySelector('[data-audio-player]');
      this.audioElement = this.querySelector('[data-audio-element]');
      this.audioPlayBtn = this.querySelector('[data-audio-play-btn]');
    }

    loadProductVariants() {
      if (!this.productVariantsScript) return;

      try {
        const data = JSON.parse(this.productVariantsScript.textContent);
        this.allVariants = data.variants || [];
        this.currentVariantId = data.currentVariantId;
      } catch (error) {
        console.error('Error loading product variants:', error);
      }
    }

    getCurrentVariant() {
      if (!this.currentVariantScript) return null;

      try {
        const variant = JSON.parse(this.currentVariantScript.textContent);
        this.currentVariantId = variant.id;
        return variant;
      } catch (error) {
        console.error('Error parsing current variant:', error);
        return null;
      }
    }

    initVariantSelectors() {
      if (!this.editionSelect || !this.colorSwatchesContainer) return;

      // Get current variant to set initial values
      const currentVariant = this.getCurrentVariant();
      if (!currentVariant) return;

      // Filter and show color swatches based on current edition
      this.filterColorSwatchesByEdition(currentVariant.option1);

      // Event listeners
      this.handleEditionChange = this.onEditionChange.bind(this);
      this.editionSelect.addEventListener('change', this.handleEditionChange);

      // Initialize color swatch listeners
      this.initColorSwatchListeners();
    }

    initColorSwatchListeners() {
      const colorInputs = this.querySelectorAll('[data-360-color-input]');
      
      colorInputs.forEach(input => {
        input.addEventListener('change', (e) => this.onColorSwatchChange(e));
      });
    }

    removeColorSwatchListeners() {
      const colorInputs = this.querySelectorAll('[data-360-color-input]');
      
      colorInputs.forEach(input => {
        input.removeEventListener('change', this.onColorSwatchChange);
      });
    }

    filterColorSwatchesByEdition(selectedEdition) {
      if (!this.colorSwatchesContainer) return;

      // Get all color swatches
      const allSwatches = this.colorSwatchesContainer.querySelectorAll('.view360-swatch');
      
      // Get variants for this edition to know which colors are available
      const availableColors = this.allVariants
        .filter(v => v.option1 === selectedEdition && !v.hide_variant)
        .map(v => v.option2);

      // Get currently selected color
      const currentColor = this.getSelectedColor();
      let needsNewSelection = false;

      // Show/hide swatches based on availability
      allSwatches.forEach(swatch => {
        const colorValue = swatch.dataset['360OptionValue'];
        
        if (availableColors.includes(colorValue)) {
          swatch.style.display = '';
          swatch.classList.remove('is-disabled');
        } else {
          swatch.style.display = 'none';
          swatch.classList.add('is-disabled');
          
          // If this was the selected color, we need to select a new one
          if (colorValue === currentColor) {
            needsNewSelection = true;
          }
        }
      });

      // Select first available color if current is not available
      if (needsNewSelection && availableColors.length > 0) {
        const firstAvailableSwatch = this.colorSwatchesContainer.querySelector(
          `.view360-swatch[data-360-option-value="${availableColors[0]}"]`
        );
        
        if (firstAvailableSwatch) {
          const input = firstAvailableSwatch.querySelector('input');
          if (input) {
            input.checked = true;
            this.updateColorDisplay(availableColors[0]);
          }
        }
      }

      // Update selected state
      this.updateSwatchSelection();
    }

    updateSwatchSelection() {
      const allSwatches = this.querySelectorAll('.view360-swatch');
      
      allSwatches.forEach(swatch => {
        const input = swatch.querySelector('input');
        if (input && input.checked) {
          swatch.classList.add('is-selected');
        } else {
          swatch.classList.remove('is-selected');
        }
      });
    }

    onEditionChange(event) {
      const selectedEdition = event.target.value;
      
      // Update color swatches based on new edition
      this.filterColorSwatchesByEdition(selectedEdition);
      
      // Always update variant when edition changes
      const newColor = this.getSelectedColor();
      const newVariant = this.findVariant(selectedEdition, newColor);
      
      if (newVariant) {
        this.updateVariant(newVariant);
      }
    }

    getSelectedColor() {
      if (!this.colorSwatchesContainer) return null;
      
      const selectedInput = this.colorSwatchesContainer.querySelector('[data-360-color-input]:checked');
      return selectedInput ? selectedInput.value : null;
    }

    onColorSwatchChange(event) {
      const selectedColor = event.target.value;
      const selectedEdition = this.editionSelect.value;

      // Update color display
      this.updateColorDisplay(selectedColor);

      // Update swatch selection
      this.updateSwatchSelection();

      // Find matching variant
      const newVariant = this.findVariant(selectedEdition, selectedColor);
      
      if (newVariant) {
        this.updateVariant(newVariant);
      }
    }

    updateColorDisplay(colorName) {
      if (this.colorDisplay) {
        this.colorDisplay.textContent = colorName;
      }
    }

    findVariant(edition, color) {
      return this.allVariants.find(v =>
        v.option1 === edition &&
        v.option2 === color &&
        !v.hide_variant
      );
    }

    updateVariant(variant) {
      if (!variant) return;

      // Stop and reset current audio
      this.stopAndResetAudio();

      this.currentVariantId = variant.id;
      this.load360ImagesForVariant(variant);
    }

    stopAndResetAudio() {
      if (this.audioElement) {
        // Pause audio if playing
        if (!this.audioElement.paused) {
          this.audioElement.pause();
        }
        // Reset playback position
        this.audioElement.currentTime = 0;
        // Update button state
        this.updatePlayButtonState(false);
      }
    }

    init360Viewer() {
      if (!this.cloudimageElement || !window.CI360) {
        console.warn('Cloudimage 360 library not loaded or no images available');
        return;
      }

      try {
        window.CI360.init();
        this.observe360LoadingState();
      } catch (error) {
        console.error('Failed to initialize 360 viewer:', error);
      }
    }

    observe360LoadingState() {
      if (!this.cloudimageElement) return;

      const observer = new MutationObserver(() => {
        const cursor = getComputedStyle(this.cloudimageElement).cursor;

        if (cursor === 'grab' || cursor === 'grabbing') {
          this.cloudimageElement.classList.add('loaded');
          this.viewerWrapper?.classList.add('loaded');
        } else if (cursor === 'wait') {
          this.cloudimageElement.classList.remove('loaded');
          this.viewerWrapper?.classList.remove('loaded');
        }
      });

      observer.observe(this.cloudimageElement, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    async load360ImagesForVariant(variant) {
      if (!variant || this.isInitializing) return;

      this.isInitializing = true;

      try {
        const currentHeight = this.viewerWrapper?.offsetHeight;
        if (this.viewerWrapper && currentHeight) {
          this.viewerWrapper.style.minHeight = `${currentHeight}px`;
        }

        this.viewerWrapper?.classList.add('loading');
        this.viewerWrapper?.classList.remove('loaded');

        const url = `/products/${this.productHandle}?variant=${variant.id}&section_id=${this.sectionId}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to load variant images');
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newViewerContainer = doc.querySelector('[data-360-container]');

        if (newViewerContainer && this.viewer360Container) {
          this.viewer360Container.innerHTML = newViewerContainer.innerHTML;

          // Re-cache cloudimage element
          this.cloudimageElement = this.querySelector('.cloudimage-360');

          if (this.cloudimageElement && window.CI360) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            window.CI360.init();
            this.observe360LoadingState();
          }
        }

        // Update audio section for new variant
        await this.updateAudioSection(doc, variant);

        this.loadingTimeout = setTimeout(() => {
          this.viewerWrapper?.classList.remove('loading');
          if (this.viewerWrapper) {
            this.viewerWrapper.style.minHeight = '';
          }
        }, 1000);
      } catch (error) {
        console.error('Error loading 360 images:', error);
        this.viewerWrapper?.classList.remove('loading');
        if (this.viewerWrapper) {
          this.viewerWrapper.style.minHeight = '';
        }
      } finally {
        this.isInitializing = false;
      }
    }

    checkInitialAudioVisibility() {
      const audioScript = this.querySelector('[data-audio-url]');
      const audioPlayer = this.querySelector('[data-audio-player]');
      if (!audioPlayer) return;

      try {
        const audioData = audioScript ? JSON.parse(audioScript.textContent) : {};
        audioPlayer.style.display = audioData.url ? 'flex' : 'none';
      } catch (error) {
        console.error('Error parsing initial audio data:', error);
        audioPlayer.style.display = 'none';
      }
    }
    /**
     * Initialize simple audio player
     */
    initAudioPlayer() {
      if (!this.audioElement || !this.audioPlayBtn) return;

      // Remove existing click listener if any
      const newAudioPlayBtn = this.audioPlayBtn.cloneNode(true);
      this.audioPlayBtn.parentNode.replaceChild(newAudioPlayBtn, this.audioPlayBtn);
      this.audioPlayBtn = newAudioPlayBtn;

      // Add new click listener
      this.audioPlayBtn.onclick = () => {
        if (this.audioElement.paused) {
          // Pause all other audio players on the page
          document.querySelectorAll('audio[data-audio-element]').forEach((audio) => {
            if (audio !== this.audioElement && !audio.paused) {
              audio.pause();
              audio.currentTime = 0;
              
              // Update other players' button states
              const otherPlayer = audio.closest('[data-audio-player]');
              if (otherPlayer) {
                const otherBtn = otherPlayer.querySelector('[data-audio-play-btn]');
                if (otherBtn) {
                  const playIcon = otherBtn.querySelector('.play-icon');
                  const pauseIcon = otherBtn.querySelector('.pause-icon');
                  if (playIcon) playIcon.style.display = 'block';
                  if (pauseIcon) pauseIcon.style.display = 'none';
                }
              }
            }
          });

          // Reset to beginning if audio has ended (currentTime equals duration)
          if (this.audioElement.currentTime >= this.audioElement.duration) {
            this.audioElement.currentTime = 0;
          }

          // Play current audio
          this.audioElement.play().catch(error => {
            console.error('Error playing audio:', error);
          });
          this.updatePlayButtonState(true);
        } else {
          // Pause current audio and reset to beginning
          this.audioElement.pause();
          this.audioElement.currentTime = 0;
          this.updatePlayButtonState(false);
        }
      };

      // Remove existing 'ended' listener and add new one
      const newAudioElement = this.audioElement.cloneNode(true);
      this.audioElement.parentNode.replaceChild(newAudioElement, this.audioElement);
      this.audioElement = newAudioElement;

      this.audioElement.addEventListener('ended', () => {
        // Reset audio to beginning when it ends
        this.audioElement.currentTime = 0;
        this.updatePlayButtonState(false);
      });
    }

    /**
     * Update audio section for new variant
     */
    async updateAudioSection(doc, variant) {
      const newAudioScript = doc.querySelector('[data-audio-url]');
      const currentAudioPlayer = this.querySelector('[data-audio-player]');

      if (!currentAudioPlayer) return;

      if (newAudioScript) {
        try {
          const audioData = JSON.parse(newAudioScript.textContent);

          // Stop current audio before updating
          this.stopAndResetAudio();

          // Re-cache audio elements
          this.audioElement = this.querySelector('[data-audio-element]');
          this.audioPlayBtn = this.querySelector('[data-audio-play-btn]');

          if (this.audioElement) {
            if (audioData.url) {
              // Update audio source
              this.audioElement.src = audioData.url;
              this.audioElement.load();
              
              // Show audio player
              currentAudioPlayer.style.display = 'flex';
              
              // Re-initialize audio player event listeners
              this.initAudioPlayer();

              // Ensure button is in paused state (no auto-play)
              this.updatePlayButtonState(false);
            } else {
              // No audio for this variant
              currentAudioPlayer.style.display = 'none';
            }
          }
        } catch (error) {
          console.error('Error parsing audio data:', error);
          currentAudioPlayer.style.display = 'none';
        }
      } else {
        // No audio data found
        this.stopAndResetAudio();
        currentAudioPlayer.style.display = 'none';
      }
    }

    /**
     * Update play/pause button state
     */
    updatePlayButtonState(isPlaying) {
      if (!this.audioPlayBtn) return;

      const playIcon = this.audioPlayBtn.querySelector('.play-icon');
      const pauseIcon = this.audioPlayBtn.querySelector('.pause-icon');

      if (isPlaying) {
        if (playIcon) playIcon.style.display = 'none';
        if (pauseIcon) pauseIcon.style.display = 'block';
      } else {
        if (playIcon) playIcon.style.display = 'block';
        if (pauseIcon) pauseIcon.style.display = 'none';
      }
    }
  }

  customElements.define('custom-360-view', Custom360View);
}

// Initialize on page load
function initCustom360Sections() {
  const sections = document.querySelectorAll('custom-360-view');
  sections.forEach(() => {
    // Instances are auto-initialized by the custom element
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustom360Sections);
} else {
  initCustom360Sections();
}

// Shopify Theme Editor support
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('custom-360-view');
    if (section && window.CI360) {
      setTimeout(() => window.CI360.init(), 100);
    }
  });
}